import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';

import { getCustomerById, updateCustomer } from 'api/customers';
import { getAllTechnicians } from 'api/technicians';
import { createPolysomnography } from 'api/polysomnographies';
import { getAllDoctors } from 'api/doctors';
import {
  NEXT_ACTIONS,
  RECORDING_STATUSES,
  STATUSES,
  MANUAL_PAYMENT_STATUSES,
} from 'config/polysomnography';
import { BLANK } from 'config/shared';

import ViewComponent from './view_component';

const PolysomnographiesAddPage = ({ history, onCountUpdate, onMessages }) => {
  const [polysomnography, setPolysomnography] = useState({
    status: STATUSES[1],
    recordingStatus: RECORDING_STATUSES[0],
    nextAction: NEXT_ACTIONS[0],
    manualPaymentStatus: MANUAL_PAYMENT_STATUSES[0],
    customer: {},
    doctor: {},
  });

  const [validation, setValidation] = useState({
    customer: true,
    customerPhone: true,
    customerEmail: true,
    customerCity: true,
    customerAddress: true,
    technician: true,
    doctor: true,
    deliveryDate: true,
    pickupDate: true,
    polysomnographyStatus: true,
    recordingStatus: true,
    nextAction: true,
    manualPaymentStatus: true,
  });

  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [originalCustomer, setOriginalCustomer] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      const allTechnicians = await getAllTechnicians();
      const allDoctors = await getAllDoctors();

      const technicianOptions = allTechnicians.map((d) => ({
        id: d.id,
        value: d.id,
        label: `${d.firstName} ${d.lastName}`,
        firstName: d.firstName,
        lastName: d.lastName,
      }));
      technicianOptions.unshift({ ...BLANK, default: true });
      setTechnicianOptions(technicianOptions);

      const doctorOptions = allDoctors
        .map((d) => ({
          id: d.id,
          value: d.id,
          label: `${d.lastName?.toUpperCase()} ${d.firstName}`,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));

      setDoctorOptions([BLANK, ...doctorOptions]);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchCustomer = async (id) => {
      const customer = await getCustomerById(id);

      setPolysomnography({
        ...polysomnography,
        customer: {
          id: customer.id,
          value: `${customer.firstName} ${customer.lastName}`,
          label: `${customer.firstName} ${customer.lastName}`,
          phone: customer.phone,
          email: customer.email,
          city: customer.city,
          address: customer.address,
        },
      });
    };

    const customerId = history.location.search?.replace('?customerId=', '');

    if (customerId) {
      fetchCustomer(customerId);
    }
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;

    setPolysomnography({
      ...polysomnography,
      [name]: value,
    });
  };

  const onCustomerChange = (e) => {
    const { customer } = polysomnography;
    const { name, value } = e.target;

    setPolysomnography({
      ...polysomnography,
      customer: {
        ...customer,
        [name]: value,
      },
    });
  };

  const onTechnicianSelect = (selectedOption) => {
    setPolysomnography({
      ...polysomnography,
      technician: selectedOption,
    });
  };

  const onDoctorSelect = (selectedOption) => {
    setPolysomnography({
      ...polysomnography,
      doctor: selectedOption,
    });

    setValidation({
      ...validation,
      doctor: !!selectedOption?.id,
    });
  };

  const onCustomerSelectChange = (customer) => {
    setOriginalCustomer({ ...customer });
    setPolysomnography({
      ...polysomnography,
      customer,
    });
  };

  const onSelectChange = (field, selectedOption) => {
    setPolysomnography({
      ...polysomnography,
      [field]: selectedOption,
    });
  };

  const onDateChange = ({ field, value }) => {
    setPolysomnography({
      ...polysomnography,
      [field]: value,
    });
  };

  const isValid = () => {
    const { customer, doctor } = polysomnography;

    const validation = {
      customer: !!customer.id,
      customerPhone: true,
      customerEmail: true,
      customerCity: true,
      customerAddress: true,
      technician: true,
      doctor: !!doctor.id,
      deliveryDate: true,
      pickupDate: true,
      polysomnographyStatus: true,
      recordingStatus: true,
      nextAction: true,
      manualPaymentStatus: true,
    };

    setValidation(validation);

    return Object.values(validation).every(Boolean);
  };

  const onSubmit = async () => {
    if (!isValid()) return;

    const {
      customer: { id, phone, email, city, address },
    } = polysomnography;

    if (
      JSON.stringify(polysomnography.customer) !==
      JSON.stringify(originalCustomer)
    ) {
      await updateCustomer(id, {
        phone,
        email,
        city,
        address,
      });
    }

    const result = await createPolysomnography({
      ...polysomnography,
      status: polysomnography.status?.value,
      recordingStatus: polysomnography.recordingStatus?.value,
      nextAction: polysomnography.nextAction?.value,
      manualPaymentStatus: polysomnography.manualPaymentStatus?.value,
      customer: polysomnography.customer?.id,
      technician: polysomnography.technician?.id,
      doctor: polysomnography.doctor?.id,
    });

    onMessages(result.meta.messages);
    onCountUpdate();

    if (!result.polysomnography?.id) {
      history.goBack();
    } else {
      history.push(`/polysomnographies/${result.polysomnography.id}`);
    }
  };

  return (
    <ViewComponent
      doctorOptions={doctorOptions}
      onChange={onChange}
      onCustomerChange={onCustomerChange}
      onDateChange={onDateChange}
      onDoctorSelect={onDoctorSelect}
      onCustomerSelectChange={onCustomerSelectChange}
      onSelectChange={onSelectChange}
      onSubmit={onSubmit}
      onTechnicianSelect={onTechnicianSelect}
      polysomnography={polysomnography}
      technicianOptions={technicianOptions}
      validation={validation}
    />
  );
};
PolysomnographiesAddPage.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  onCountUpdate: PropTypes.func.isRequired,
  onMessages: PropTypes.func.isRequired,
};
export default PolysomnographiesAddPage;
